import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getAreaListThunk,
    getEmployeePositionGetCurrentPositionThunk,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getMotivationInfoThunk,
    getRouteMyRoutesThunk,
    getTaskListsThunk,
    getUserMeThunk,
    getUserTeamGetByManagerThunk,
    getWorkCheckThunk,
    getWorkGetScheduleThunk,
    postWorkStartThunk,
} from "tm-shared/api";
import { UserRole, WorkStatus } from "tm-shared/types";
import { warmupImages } from "tm-shared/utils";

import { useDailyResetEffect } from "@/app/AppInitializer/hooks/useDailyResetEffect";
import { useTaCheckoutPrefetchEffect } from "@/app/AppInitializer/hooks/useTaCheckoutPrefetchEffect";
import { useUserResetEffect } from "@/app/AppInitializer/hooks/useUserResetEffect";
import { useCheckYesterdayOpenCheckouts } from "@/hooks/app/useCheckYesterdayOpenCheckouts";
import {
    QueueTaskStatus,
    useAsyncTaskQueue,
} from "@/hooks/core/useAsyncTaskQueue";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { opfsQueueStorage } from "@/Services/OPFSQueueStorage";
import { ProgressLoader } from "@/shared/feedback";
import { getCatalogProductsThunk } from "@/store/slices/catalog";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/manager";
import {
    bulkAddRequestQueueItems,
    getKpiGetPointProgressesThunk,
    getPlansByPointIdThunk,
    setIsInitialDataLoading,
    setLastOfflineUpdate,
} from "@/store/slices/offline";
import { setInitFailedRequests } from "@/store/slices/ui";
import { loadConfigFromServer, loadProgressFromServer } from "@/store/thunks";
import { getFileUrl, getKpiTargetDate } from "@/utils";

const COOKIE_CONFIG = {
    name: "FILE_ACCESS",
    value: "0184be96-7e48-4a20-bc4d-5bccb68a91c3",
    options: { domain: ".trademaster.in.ua", path: "/", secure: true },
} as const;

type AppInitContext = {
    roleSlug: UserRole;
    userId: string;
    workStatus: string;
    routeId?: string;
    employeePositionId: string;
};

type RoleInitHandler = (ctx: AppInitContext) => Promise<void>;

const createKpiParams = (employeeId: string, roleSlug: UserRole) => ({
    employeeId,
    targetDate: getKpiTargetDate(),
    roleSlug,
});

export const AppInitializer = () => {
    const { queue: startDayQueue, progress: queueProgress } =
        useAsyncTaskQueue();

    const [startDayLoading, setStartDayLoading] = useState(false);

    const lastOfflineUpdate = useAppSelector(
        (s) => s.offline.lastOfflineUpdate,
    );
    const offlineMode = useOfflineModeSelector();

    const dispatch = useAppDispatch();
    const isInitializing = useRef(false);

    const { handleCheck: handleCheckYesterdayOpenCheckouts } =
        useCheckYesterdayOpenCheckouts();

    useUserResetEffect();
    useDailyResetEffect();
    useTaCheckoutPrefetchEffect();

    const setCookie = useCallback(() => {
        Cookies.set(
            COOKIE_CONFIG.name,
            COOKIE_CONFIG.value,
            COOKIE_CONFIG.options,
        );
    }, []);

    const fetchTaOfflineData: RoleInitHandler = useCallback(
        async ({ userId, workStatus, routeId, employeePositionId }) => {
            const commonKpiParams = createKpiParams(
                employeePositionId,
                UserRole.Ta,
            );

            startDayQueue.add(
                "Завантаження зарплати",
                () =>
                    dispatch(
                        getKpiGetSalaryToPayThunk(commonKpiParams),
                    ).unwrap(),
                { type: "getKpiGetSalaryToPayThunk" },
            );

            startDayQueue.add(
                "Завантаження прогресу KPI",
                () =>
                    dispatch(getKpiGetProgressThunk(commonKpiParams)).unwrap(),
                { type: "getKpiGetProgressThunk" },
            );

            startDayQueue.add(
                "Завантаження інформації про мотивацію",
                () =>
                    dispatch(
                        getMotivationInfoThunk({
                            employeeId: employeePositionId,
                        }),
                    ).unwrap(),
                { type: "getMotivationInfoWorkThunk" },
            );

            if (workStatus === WorkStatus.StartWork) {
                startDayQueue.add("Завантаження розкладу роботи", async () => {
                    const { result } = await dispatch(
                        getWorkGetScheduleThunk({ userId }),
                    ).unwrap();

                    const pointIds = Object.keys(result?.points ?? {});

                    if (pointIds.length) {
                        startDayQueue.add(
                            "Завантаження інформації про задачі",
                            () =>
                                dispatch(
                                    getTaskListsThunk({ targetIds: pointIds }),
                                ).unwrap(),
                        );

                        if (routeId) {
                            startDayQueue.add(
                                "Завантаження інформації про плани на ТТ",
                                async () => {
                                    await dispatch(
                                        getPlansByPointIdThunk({
                                            pointIds,
                                            routeId,
                                        }),
                                    );

                                    startDayQueue.add(
                                        "Завантаження інформації про факти на ТТ",
                                        () =>
                                            dispatch(
                                                getKpiGetPointProgressesThunk({
                                                    pointIds,
                                                }),
                                            ),
                                    );
                                },
                            );
                        }
                    }

                    handleCheckYesterdayOpenCheckouts();
                });
            }
        },
        [dispatch, handleCheckYesterdayOpenCheckouts, startDayQueue],
    );

    const fetchSvOfflineData: RoleInitHandler = useCallback(
        async ({ userId, workStatus, employeePositionId }) => {
            const commonKpiParams = createKpiParams(
                employeePositionId,
                UserRole.Sv,
            );

            if (workStatus !== WorkStatus.StartWork) {
                startDayQueue.add("Початок робочого дня", () =>
                    dispatch(
                        postWorkStartThunk({ userId, roleSlug: UserRole.Sv }),
                    ).unwrap(),
                );
            }

            startDayQueue.add("Завантаження інформації про команду", () =>
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: userId }),
                ).unwrap(),
            );

            startDayQueue.add(
                "Завантаження інформації про маршрути команди",
                () =>
                    dispatch(
                        getRouteMyRoutesThunk({
                            ownerId: userId,
                            roleSlug: UserRole.Ta,
                        }),
                    ).unwrap(),
            );

            startDayQueue.add("Завантаження зарплати", () =>
                dispatch(getKpiGetSalaryToPaySvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження прогресу KPI", () =>
                dispatch(getKpiGetProgressSvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження інформації про мотивацію", () =>
                dispatch(
                    getMotivationInfoThunk({
                        employeeId: employeePositionId,
                    }),
                ).unwrap(),
            );
        },
        [dispatch, startDayQueue],
    );

    const fetchHosOfflineData: RoleInitHandler = useCallback(
        async ({ employeePositionId }) => {
            const commonKpiParams = createKpiParams(
                employeePositionId,
                UserRole.Sv,
            );

            startDayQueue.add("Завантаження зарплати", () =>
                dispatch(getKpiGetSalaryToPaySvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження прогресу KPI", () =>
                dispatch(getKpiGetProgressSvThunk(commonKpiParams)).unwrap(),
            );

            startDayQueue.add("Завантаження інформації про мотивацію", () =>
                dispatch(
                    getMotivationInfoThunk({
                        employeeId: employeePositionId,
                    }),
                ).unwrap(),
            );
        },
        [dispatch, startDayQueue],
    );

    const fetchMdOfflineData: RoleInitHandler = useCallback(
        async ({ userId, workStatus }) => {
            if (workStatus === WorkStatus.StartWork) {
                startDayQueue.add("Завантаження розкладу роботи", async () => {
                    const { result } = await dispatch(
                        getWorkGetScheduleThunk({ userId }),
                    ).unwrap();

                    const pointIds = Object.keys(result?.points ?? {});

                    if (pointIds.length) {
                        startDayQueue.addAndRun(
                            "Завантаження інформації про задачі",
                            () =>
                                dispatch(
                                    getTaskListsThunk({ targetIds: pointIds }),
                                ).unwrap(),
                        );
                    }
                });
            }
        },
        [dispatch, startDayQueue],
    );

    const fetchSvmOfflineData: RoleInitHandler = useCallback(
        async ({ userId, workStatus }) => {
            if (workStatus !== WorkStatus.StartWork) {
                startDayQueue.add("Початок робочого дня", () =>
                    dispatch(
                        postWorkStartThunk({ userId, roleSlug: UserRole.Svm }),
                    ).unwrap(),
                );
            }

            startDayQueue.add("Завантаження інформації про команду", () =>
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: userId }),
                ).unwrap(),
            );

            startDayQueue.add(
                "Завантаження інформації про маршрути команди",
                () =>
                    dispatch(
                        getRouteMyRoutesThunk({
                            ownerId: userId,
                            roleSlug: UserRole.Md,
                        }),
                    ).unwrap(),
            );
        },
        [dispatch, startDayQueue],
    );

    const roleInitMap = useMemo<Partial<Record<UserRole, RoleInitHandler>>>(
        () => ({
            [UserRole.Md]: fetchMdOfflineData,
            [UserRole.Kam]: fetchMdOfflineData,
            [UserRole.Sv]: fetchSvOfflineData,
            [UserRole.Hos]: fetchHosOfflineData,
            [UserRole.Svm]: fetchSvmOfflineData,
            [UserRole.Ta]: fetchTaOfflineData,
        }),
        [
            fetchMdOfflineData,
            fetchSvOfflineData,
            fetchHosOfflineData,
            fetchSvmOfflineData,
            fetchTaOfflineData,
        ],
    );

    const loadInitContext = useCallback(async (): Promise<AppInitContext> => {
        const userData = await startDayQueue.addAndRun(
            "Отримання даних користувача",
            async () => {
                const { result: resultUserMe } =
                    await dispatch(getUserMeThunk()).unwrap();

                if (!resultUserMe.authorizedRole) {
                    throw new Error("authorizedRole is null");
                }

                const { result: resultEmployee } = await dispatch(
                    getEmployeePositionGetCurrentPositionThunk({
                        role: resultUserMe.authorizedRole.slug,
                        userId: resultUserMe.userId,
                    }),
                ).unwrap();

                return {
                    roleSlug: resultUserMe.authorizedRole.slug,
                    userId: resultUserMe.userId,
                    routeId: resultEmployee?.selfRoute?.uuid,
                    employeePositionId: resultEmployee.id,
                };
            },
        );

        const { result: resultWorkCheck } = await startDayQueue.addAndRun(
            "Перевірка статусу роботи",
            () =>
                dispatch(
                    getWorkCheckThunk({
                        userId: userData.userId,
                        roleSlug: userData.roleSlug,
                    }),
                ).unwrap(),
        );

        return { ...userData, workStatus: resultWorkCheck };
    }, [dispatch, startDayQueue]);

    const enqueueCommonInit = useCallback(
        ({ userId, roleSlug }: AppInitContext) => {
            startDayQueue.add("Синхронізація прогресу", async () => {
                dispatch(loadConfigFromServer({ userId, roleSlug }));
                await dispatch(
                    loadProgressFromServer({ userId, roleSlug }),
                ).unwrap();

                // restore unfinished queue items from previous days
                const groups = await opfsQueueStorage.loadPendingFromPastDays(
                    userId,
                    roleSlug,
                );
                const items = groups.flatMap((g) => g.items);
                if (items.length > 0) {
                    dispatch(bulkAddRequestQueueItems(items));
                }
            });

            startDayQueue.add(
                "Отримання доступних регіонів",
                () => dispatch(getAreaListThunk()).unwrap(),
                { type: "getAreaListThunk" },
            );

            startDayQueue.add(
                "Завантаження каталогу",
                async () => {
                    const productsMap = await dispatch(
                        getCatalogProductsThunk(),
                    ).unwrap();

                    const imageUrls = Object.values(productsMap)
                        .map((p) => p.params.image.value.map(getFileUrl))
                        .flat();

                    warmupImages(imageUrls);
                },
                { type: "getCatalogExportThunk" },
            );
        },
        [dispatch, startDayQueue],
    );

    const runRoleInit = useCallback(
        async (ctx: AppInitContext) => {
            const initRole = roleInitMap[ctx.roleSlug];

            if (initRole) {
                await initRole(ctx);
            }
        },
        [roleInitMap],
    );

    const runQueue = useCallback(async () => {
        try {
            await startDayQueue.runParallel();

            const failedTypes = startDayQueue.progress.tasks.reduce<string[]>(
                (acc, task) => {
                    if (task.status === QueueTaskStatus.Completed) return acc;

                    const type = task?.metadata?.type;
                    if (!type) return acc;

                    return acc.includes(type) ? acc : [...acc, type];
                },
                [],
            );

            dispatch(setInitFailedRequests(failedTypes));
        } catch (error) {
            console.error("Init error:", error);
        } finally {
            dispatch(setLastOfflineUpdate(new Date().toISOString()));
            isInitializing.current = false;

            setTimeout(() => {
                setStartDayLoading(false);
                startDayQueue.clear();

                dispatch(setIsInitialDataLoading(false));
            }, 2000);
        }
    }, [dispatch, startDayQueue]);

    useEffect(() => {
        if (isInitializing.current || offlineMode) return;

        if (
            lastOfflineUpdate &&
            dayjs(lastOfflineUpdate).isSame(dayjs(), "day")
        ) {
            return;
        }

        const init = async () => {
            setStartDayLoading(true);
            isInitializing.current = true;

            try {
                const ctx = await loadInitContext();

                enqueueCommonInit(ctx);
                await runRoleInit(ctx);
                await runQueue();
            } catch {
                setStartDayLoading(false);
                isInitializing.current = false;
                startDayQueue.clear();
            }
        };

        setCookie();

        void init();
    }, [
        enqueueCommonInit,
        lastOfflineUpdate,
        loadInitContext,
        offlineMode,
        runQueue,
        runRoleInit,
        setCookie,
        startDayQueue,
    ]);

    return (
        <ProgressLoader
            open={startDayLoading || queueProgress.isRunning}
            tasks={queueProgress.tasks}
        />
    );
};
